/** @jsxImportSource @emotion/react */
import { ReactNode } from 'react'

type Props = {
  /** When true, every form control inside is disabled (read-only view mode). */
  readOnly: boolean
  children: ReactNode
  className?: string
}

/**
 * Wraps a form body so that, in read-only mode, all native controls are disabled
 * via `<fieldset disabled>` and non-native controls (react-select) are neutralized
 * with `pointer-events: none`. Keep action buttons (Save/Delete) OUTSIDE this
 * wrapper and hide them separately; navigation buttons/links stay usable.
 *
 * Used to give internal users without an area's edit permission a view-only page
 * instead of being bounced away (TES-2881).
 */
export function ReadOnlyFieldset({ readOnly, children, className }: Props) {
  return (
    <fieldset
      disabled={readOnly}
      className={className}
      // fieldset carries default browser styling + intrinsic min-width; reset it.
      css={{
        border: 0,
        padding: 0,
        margin: 0,
        minWidth: 0,
        ...(readOnly && {
          // react-select renders a div, not a native control, so fieldset:disabled
          // won't stop it opening — block pointer interaction on its control.
          '& .select__control, & [class*="-control"]': { pointerEvents: 'none' },
        }),
      }}>
      {children}
    </fieldset>
  )
}

export default ReadOnlyFieldset
